import { useQuery } from "@tanstack/react-query";

import type {
  ExerciseComparison,
  WorkoutComparison,
} from "../../../shared/src";
import { getWorkoutComparison } from "../api-client";

export function WorkoutCompareBand({ workoutId }: { workoutId: number }) {
  const query = useQuery({
    queryKey: ["workoutCompare", workoutId] as const,
    queryFn: () => getWorkoutComparison(workoutId),
  });

  const data = query.data;
  if (!data) return null;

  const rows = data.exercises.filter((e) => e.previous !== null);
  if (rows.length === 0) return null;

  return (
    <section
      role="region"
      aria-label="vs last session"
      className="rounded-lg border border-slate-800 bg-slate-900 p-3"
    >
      <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">
        vs last session
      </p>
      <ul className="divide-y divide-slate-800 text-sm">
        {rows.map((ex) => (
          <li
            key={ex.exercise_id}
            className="flex items-baseline justify-between gap-3 py-1.5"
          >
            <span className="text-slate-200">{ex.exercise_name}</span>
            <span className="flex items-baseline gap-3 text-xs tabular-nums">
              <DeltaBadge
                label="Vol"
                current={ex.current.total_volume_kg}
                previous={ex.previous?.totals.total_volume_kg ?? 0}
                unit="kg"
              />
              <DeltaBadge
                label="e1RM"
                current={ex.current.top_set?.e1rm ?? 0}
                previous={ex.previous?.totals.top_set?.e1rm ?? 0}
                unit="kg"
                precision={1}
              />
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function DeltaBadge({
  label,
  current,
  previous,
  unit,
  precision = 0,
}: {
  label: string;
  current: number;
  previous: number;
  unit: string;
  precision?: number;
}) {
  const delta = current - previous;
  const sign = delta > 0 ? "+" : delta < 0 ? "" : "±";
  const colour =
    delta > 0
      ? "text-orange-400"
      : delta < 0
        ? "text-rose-400"
        : "text-slate-400";
  const display = `${sign}${delta.toFixed(precision)}`;
  return (
    <span className="flex items-baseline gap-1">
      <span className="text-slate-500">{label}</span>
      <span className={colour} data-testid={`delta-${label.toLowerCase()}`}>
        {display}
        {unit}
      </span>
    </span>
  );
}

export type { ExerciseComparison, WorkoutComparison };
